"use client";
import React, { useState, useEffect } from "react";
import PressTabs from "./PressTabs";
import PressList from "./PressList";
import PrimaryBtn from "@/components/PrimaryBtn";
import Loader from "@/components/Loader";

import { Icons } from "../assets/assets";
import { getMediaInterViewList } from "@/lib/ApiClient";

interface PressItem {
  categoryId: number;
  id: number;
  logo: string;
  publishedDate: string;
  publishedText: string;
  publishedUrl: string;
  smallOverview: string;
  title: string;
}

type MediaKey = "PRESS_RELEASE" | "MANAGEMENT_INTERVIEW";

export default function Press_release() {
  const [items, setItems] = useState<PressItem[]>([]);
  const [active, setActive] = useState<MediaKey>("PRESS_RELEASE");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const fetchMedia = React.useCallback(
    async (key: MediaKey, pageNo: number, append = false): Promise<void> => {
      try {
        setLoading(true);

        const data: any = await getMediaInterViewList(
          pageNo,
          limit,
          key
        );

        const items = data?.items ?? [];

        if (items.length === 0) {
          setHasMore(false);
          return;
        }

        setItems((prev) => (append ? [...prev, ...items] : items));
        setHasMore(items.length === limit);
      } catch (err) {
        console.error(
          err instanceof Error ? err.message : "Failed to fetch media"
        );
      } finally {
        setLoading(false);
      }
    },
    [limit] // ✅ only stable dependency
  );

  /** Initial Load */
  useEffect(() => {
    fetchMedia(active, 1);
  }, [active, fetchMedia]);

  /** Tab Change */
  const handleChange = async (key: MediaKey) => {
    setActive(key);
    setPage(1);
    setHasMore(true);
    setItems([]);
    await fetchMedia(key, 1);
  };

  /** View More */
  const handleViewMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    await fetchMedia(active, nextPage, true);
  };

  return (
    <section className="lg:py-[150px] py-[50px] bg-[#fff]">
      {/* Header */}
      <div className="press-header max-w-[2500px] mx-auto w-[calc(100%_-_10%)] border-b border-white pb-[20px] flex flex-col lg:flex-row lg:items-start gap-[15px] justify-between">
        <div>
          <h5 className="text-[#636363] text-base font-medium uppercase pb-4">
            Services
          </h5>
          <h2 className="text-[length:var(--section-title)] font-medium clash-font leading-[110%]">
            <span className="text-[#00040b]">Stories That Reflect Our</span>
            <br />
            <span className="text-[#225bd4]">Growth and Dedication</span>
          </h2>
        </div>

        <p className="text-[#00040b]/70 max-w-[60ch] text-lg leading-[150%] mt-2 xl:mt-13">
          Stay updated with latest management interviews and press releases that
          highlight our dedication, milestones, and product launches. From
          in-depth leadership insights to major achievements, these media
          coverages capture our values, visions, and expertise.
        </p>
      </div>

      {/* Tabs */}
      <div className="max-w-[1400px] mx-auto pt-[54px] px-5">
        <PressTabs active={active} onChange={handleChange} />
      </div>

      {/* List */}
      <div className="max-w-[1400px] min-[2000px]:max-w-[2000px] mx-auto pt-[54px] px-5">
        {loading && page === 1 ? (
          <div className="flex justify-center py-10">
            <Loader size={80} />
          </div>
        ) : items.length ? (
          <PressList items={items} />
        ) : (
          <p className="text-center text-gray-500 py-10">No Items Found</p>
        )}
      </div>

      {/* View More */}
      {hasMore && (
        <div className="max-w-[1400px] mx-auto pt-[54px] text-center">
          <PrimaryBtn
            text={loading ? "Loading..." : "View More Press Releases"}
            iconSrc={Icons?.chevron}
            dark={false}
            onClick={handleViewMore}
          />
        </div>
      )}
    </section>
  );
}
